import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { AppDialog } from "../../components/AppDialog";
import { useNotify } from "../../components/notifications";
import { useConfirm } from "../../components/confirm";
import { AiCombo } from "../../components/AiCombo";

/**
 * Test Code → Method Defaults — the three tabs' toolbars and their dialogs
 * (prototype v12 `methodDefaultsHTML`).
 *
 * The bar differs PER TAB because the three tabs store different things, and
 * the prototype says so:
 *
 * | Tab         | Bar                        | Why                                 |
 * |-------------|----------------------------|-------------------------------------|
 * | QC Types    | + Add · Remove             | a pure M-M LINK — you attach or detach an existing QC type, there is nothing to edit here |
 * | Test Guide  | New · Edit · Delete        | owned rows in `dbo.TestGuide`       |
 * | Prep Method | New · Edit · Delete        | owned rows in `dbo.PrepMethod`      |
 *
 * `Export to` is NOT repeated here: the shared `<AppDataGrid>` already renders
 * Export + Maximize in its own header strip on every detail grid (CLAUDE.md
 * §18.6 "Detail-view grid header tools"), so a second button would be two
 * exports of the same rows.
 *
 * ⚠ **Retire / Reactivate are absent from QC Types on purpose.** The prototype
 * offers them, but `dbo.TestMethodTestMethods_QCTypeQCTypes` is a bare link
 * table — `OID · TestMethods · QCTypes · OptimisticLockField` — with no
 * RetireDate and no GCRecord, and no column may be added (owner constraint
 * 2026-10-12). The only other place to put a retire date is the `dbo.QCType`
 * MASTER, which would retire that QC type for every test in the lab. Rendering
 * the buttons dead would be worse than leaving them out.
 */

type Opt = { id: string; name: string };

/** Loads one lookup list once per mount. These are small masters (<200 rows). */
function useLookup(url: string | null) {
  const [opts, setOpts] = useState<Opt[]>([]);
  useEffect(() => {
    if (!url) return;
    let alive = true;
    void (async () => {
      try {
        const { data } = await api.get(url);
        const rows = data?.data ?? data ?? [];
        if (alive) setOpts(Array.isArray(rows) ? rows : []);
      } catch {
        // A lookup that fails to load leaves its dropdown empty rather than
        // taking the dialog down; the shared axios handling already reported it.
        if (alive) setOpts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return opts;
}

/**
 * Every dropdown in these dialogs. It is the SHARED `<AiCombo>` — the same
 * control the detail form's own Matrix / Method / Department use — not a native
 * `<select>`, which renders with the browser's own chrome and the stylesheet's
 * yellow default background and so looks nothing like the form around it
 * (owner 2026-10-14: "always use the standard format for every control").
 *
 * One wrapper rather than an AiCombo at each call site, so the Test Guide and
 * Prep Method dialogs cannot drift apart.
 */
function Select({
  value,
  onChange,
  options,
  required,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Opt[];
  required?: boolean;
  disabled?: boolean;
}) {
  return (
    <AiCombo
      value={value || null}
      options={options.map((o) => ({ value: o.id, label: o.name }))}
      placeholder="— Select —"
      disabled={disabled}
      className={required && !value ? "required-empty" : undefined}
      onChange={(v) => onChange(v ?? "")}
    />
  );
}

/* ── QC Types → + Add ─────────────────────────────────────────────────────── */

/** Picker of the QC types NOT yet linked to this test. The server computes
 *  "available", so a type linked by someone else a second ago cannot be
 *  offered twice; the link insert is idempotent as well. */
export function QcTypeAddDialog({
  open,
  base,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  onClose: () => void;
  onDone: (added: number) => void;
}) {
  const [opts, setOpts] = useState<Opt[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const notify = useNotify();

  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    setQ("");
    void (async () => {
      try {
        const { data } = await api.get(`${base}/qc-types/available`);
        setOpts(data?.data ?? []);
      } catch {
        setOpts([]);
      }
    })();
  }, [open, base]);

  if (!open) return null;
  const shown = opts.filter((o) => !q.trim() || o.name.toLowerCase().includes(q.trim().toLowerCase()));

  const save = async () => {
    if (!picked.size) {
      notify.warning("Select at least one QC type.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`${base}/qc-types/link`, { qcTypeIds: [...picked] });
      onDone(picked.size);
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not add the QC types.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Add QC Types"
      width={520}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>
            Add{picked.size ? ` (${picked.size})` : ""}
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <input
        /* A search box is not an editable data field, so it drops the yellow
           required tint — the shared opt-out the link pickers already use
           (owner request 2026-10-27). */
        className="sd-input sd-assoc-search"
        placeholder="Search QC types…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="tc-setcat-list" style={{ marginTop: 8, maxHeight: 320 }}>
        {shown.map((o) => (
          <label key={o.id}>
            <input
              type="checkbox"
              checked={picked.has(o.id)}
              onChange={() =>
                setPicked((p) => {
                  const n = new Set(p);
                  if (n.has(o.id)) n.delete(o.id);
                  else n.add(o.id);
                  return n;
                })
              }
            />
            {o.name}
          </label>
        ))}
        {!shown.length && (
          <div className="tc-setcat-empty">
            {opts.length
              ? "No QC type matches that search."
              : "Nothing left to add — every QC type is already on this test."}
          </div>
        )}
      </div>
    </AppDialog>
  );
}

/* ── Test Guide → New / Edit ──────────────────────────────────────────────── */

export type GuideRow = {
  oid?: string;
  id?: string;
  containerId?: string | null;
  preservativeId?: string | null;
  temperatureId?: string | null;
  holdingTimeBeforePrepId?: string | null;
  holdingTimeBeforeAnalysisId?: string | null;
  setPrepTimeAsAnalysisTime?: boolean;
  monitorHoldTime?: boolean;
  warnBeforeHours?: number | null;
};

/** The prototype's `guideDlg`, field for field and in its order. Prefills from
 *  the row's FK **ids**, never its display names — the lookup masters have no
 *  unique name, so matching by text could silently repoint the row. */
export function TestGuideDialog({
  open,
  base,
  row,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  /** null = New. */
  row: GuideRow | null;
  onClose: () => void;
  onDone: (created: boolean) => void;
}) {
  const root = base.replace(/\/test-methods\/.*/, "/test-methods");
  const containers    = useLookup(open ? `${root}/lookups/container` : null);
  const preservatives = useLookup(open ? `${root}/lookups/preservative` : null);
  const temperatures  = useLookup(open ? `${root}/lookups/temperature` : null);

  const [f, setF] = useState<GuideRow>({});
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (open) setF(row ? { ...row } : { setPrepTimeAsAnalysisTime: false, monitorHoldTime: false, warnBeforeHours: 0 });
  }, [open, row]);

  if (!open) return null;
  const set = <K extends keyof GuideRow>(k: K, v: GuideRow[K]) => setF((p) => ({ ...p, [k]: v }));
  const guideId = row?.oid ?? row?.id;

  const save = async () => {
    // Verbatim from the prototype's own check.
    if (!f.containerId || !f.preservativeId) {
      notify.warning("Container and Preservative are required.");
      return;
    }
    if (!(await confirm({ title: "Test Guide", message: "Do you want to Save?" }))) return;
    const body = {
      containerId: f.containerId,
      preservativeId: f.preservativeId,
      temperatureId: f.temperatureId || null,
      holdingTimeBeforePrepId: f.holdingTimeBeforePrepId || null,
      holdingTimeBeforeAnalysisId: f.holdingTimeBeforeAnalysisId || null,
      setPrepTimeAsAnalysisTime: !!f.setPrepTimeAsAnalysisTime,
      monitorHoldTime: !!f.monitorHoldTime,
      warnBeforeHours: f.warnBeforeHours === null || f.warnBeforeHours === undefined ? null : Number(f.warnBeforeHours),
    };
    setBusy(true);
    try {
      if (guideId) await api.put(`${base}/test-guide/${guideId}`, body);
      else await api.post(`${base}/test-guide`, body);
      onDone(!guideId);
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not save the test guide.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Test Guide"
      width={520}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>
            Save
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <div className="tc-copy-grid tc-guide-grid">
        <label className="tc-copy-lbl">Container<span className="req">*</span></label>
        <Select value={f.containerId ?? ""} onChange={(v) => set("containerId", v)} options={containers} required />

        <label className="tc-copy-lbl">Preservative<span className="req">*</span></label>
        <Select value={f.preservativeId ?? ""} onChange={(v) => set("preservativeId", v)} options={preservatives} required />

        <label className="tc-copy-lbl">Temperature</label>
        <Select value={f.temperatureId ?? ""} onChange={(v) => set("temperatureId", v)} options={temperatures} />

      </div>
    </AppDialog>
  );
}

/* ── Prep Method → New / Edit ─────────────────────────────────────────────── */

export type PrepRow = {
  oid?: string;
  methodId?: string | null;
  prepTypeId?: string | null;
  instrumentId?: string | null;
  department?: string | null;
  description?: string | null;
  tier?: number | null;
  sort?: number | null;
  activeDate?: string | null;
  retireDate?: string | null;
};

const dateOnly = (v: unknown) => (v ? String(v).slice(0, 10) : "");

/** The prototype's `prepDlg`. Tier, Sort and Department are rendered read-only
 *  exactly as the prototype renders them — and they are NOT sent: the server
 *  derives Tier and Sort on insert and keeps them on edit, so the number on
 *  screen and the number in the table cannot drift apart.
 *
 *  Instrument is a picker over `dbo.Labware`, not the prototype's free-text box:
 *  `dbo.PrepMethod.Instrument` is declared nvarchar but every reader joins it to
 *  `Labware.Oid`, so free text would break the whole grid with a conversion
 *  error. Deliberate deviation. */
export function PrepMethodDialog({
  open,
  base,
  row,
  nextTier,
  nextSort,
  department,
  defaultMethodId,
  onClose,
  onDone,
}: {
  open: boolean;
  base: string;
  row: PrepRow | null;
  nextTier: number;
  nextSort: number;
  department?: string | null;
  defaultMethodId?: string | null;
  onClose: () => void;
  onDone: (created: boolean) => void;
}) {
  const root = base.replace(/\/test-methods\/.*/, "/test-methods");
  const methods = useLookup(open ? `${root}/methods` : null);
  const prepTypes = useLookup(open ? `${root}/lookups/prep-types` : null);
  const instruments = useLookup(open ? `${root}/lookups/instrument` : null);

  const [f, setF] = useState<PrepRow>({});
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const { confirm } = useConfirm();

  useEffect(() => {
    if (!open) return;
    setF(
      row
        ? { ...row, activeDate: dateOnly(row.activeDate), retireDate: dateOnly(row.retireDate) }
        : { methodId: defaultMethodId ?? "", activeDate: "", retireDate: "" },
    );
  }, [open, row, defaultMethodId]);

  if (!open) return null;
  const set = <K extends keyof PrepRow>(k: K, v: PrepRow[K]) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.methodId || !f.prepTypeId) {
      notify.warning("Method and Prep Type are required.");
      return;
    }
    if (!(await confirm({ title: "Prep Method", message: "Do you want to Save?" }))) return;
    const body = {
      methodId: f.methodId,
      prepTypeId: f.prepTypeId,
      instrumentId: f.instrumentId || null,
      description: f.description || null,
      activeDate: f.activeDate || null,
      retireDate: f.retireDate || null,
    };
    setBusy(true);
    try {
      if (row?.oid) await api.put(`${base}/test-code/prep-methods/${row.oid}`, body);
      else await api.post(`${base}/test-code/prep-methods`, body);
      onDone(!row?.oid);
      onClose();
    } catch (e: unknown) {
      notify.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
          "Could not save the prep method.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppDialog
      open={open}
      title="Prep Method"
      width={640}
      onClose={onClose}
      className="dlg-center"
      footer={
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={busy}>
            Save
          </button>
          <button className="ca-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <div className="tc-copy-grid tc-prep-grid">
        <label className="tc-copy-lbl">Method<span className="req">*</span></label>
        <Select value={f.methodId ?? ""} onChange={(v) => set("methodId", v)} options={methods} required />

        <label className="tc-copy-lbl">Description</label>
        <input
          className="sd-input"
          value={f.description ?? ""}
          onChange={(e) => set("description", e.target.value)}
        />

        <label className="tc-copy-lbl">Prep Type<span className="req">*</span></label>
        <Select value={f.prepTypeId ?? ""} onChange={(v) => set("prepTypeId", v)} options={prepTypes} required />

        <label className="tc-copy-lbl">Active Date</label>
        <input
          className="sd-input"
          type="date"
          value={f.activeDate ?? ""}
          onChange={(e) => set("activeDate", e.target.value)}
        />

        <label className="tc-copy-lbl">Tier</label>
        <input
          className="sd-input"
          readOnly
          value={row ? (f.tier ?? "") : nextTier}
          title="Tier = prep step order + 1 (the analysis itself is tier 1). Set by the server."
        />

        <label className="tc-copy-lbl">Instrument</label>
        <Select value={f.instrumentId ?? ""} onChange={(v) => set("instrumentId", v)} options={instruments} />

        <label className="tc-copy-lbl">Department</label>
        <input className="sd-input" readOnly value={department || "N/A"} title="Inherited from the test." />

        <label className="tc-copy-lbl">Sort</label>
        <input
          className="sd-input"
          readOnly
          value={row ? (f.sort ?? "") : nextSort}
          title="The step's position. Set by the server when the step is added."
        />

        <label className="tc-copy-lbl">Retire Date</label>
        <input
          className="sd-input"
          type="date"
          value={f.retireDate ?? ""}
          onChange={(e) => set("retireDate", e.target.value)}
        />
        <span />
        <span />
      </div>
    </AppDialog>
  );
}
